/**
 * The assistant's knowledge base and its matcher.
 *
 * Kept out of the component so the matching can be exercised directly — a
 * retrieval assistant that silently fails to match is worse than no assistant,
 * because it answers "I don't know" to questions it demonstrably does know.
 */
import { inr, fmtDate, dayOfWeek } from './format.js';

export const SUGGESTIONS = [
  'How many leaves do I have left?',
  'What is the work-from-home policy?',
  'When will my salary be credited?',
  'My biometric punch is missing',
  'Upcoming holidays',
  'How does the notice period work?',
];

/**
 * Each entry scores against the question by keyword length, exactly as the
 * prototype did — longer, more specific phrases outrank incidental words.
 */
const KB = [
  {
    q: ['leave balance', 'how many leave', 'remaining leave', 'leave left', 'leaves left', 'leave available'],
    source: 'Leave Policy · your leave ledger',
    answer: ({ balances }) => {
      if (!balances?.length) return 'I could not read your leave ledger just now. Try the Leave page, which shows the same figures.';
      const rows = balances
        .map((b) => `<li><b>${esc(b.leaveType.name)}</b> — ${esc(b.available)} of ${esc(b.entitled)} days left${b.pending ? ` (${esc(b.pending)} pending approval)` : ''}</li>`)
        .join('');
      return `Here is your leave balance as it stands today:<ul>${rows}</ul>Earned Leave carries forward; Casual and Sick Leave lapse at the end of the leave year.`;
    },
  },
  {
    q: ['wfh', 'work from home', 'hybrid', 'remote policy', 'working from home'],
    source: 'Work From Home & Hybrid Policy',
    answer: () => `Under the current hybrid policy:<ul><li>Counselling and Recruitment CRM — <b>4 days</b> from office</li><li>Digital Marketing — <b>3 days</b> from office</li><li>WFH days must be marked before <b>09:30</b> with geo check-in enabled</li><li>Managers can approve up to <b>5 exception days</b> a month</li></ul>Full-time WFH is approved case by case by the department head together with HR.`,
  },
  {
    q: ['payslip', 'salary slip', 'salary credited', 'when is salary', 'net pay', 'my salary'],
    source: 'Payroll module · your salary record',
    answer: ({ payslips }) => {
      if (!payslips?.length) return 'No payslip has been released to you yet. They appear under Payroll once a run is processed.';
      const latest = payslips[0];
      return `Your most recent payslip is for <b>${esc(latest.period)}</b>: net <b>${esc(inr(latest.netPay))}</b> on ${esc(latest.paidDays)} paid days${latest.lopDays ? `, with ${esc(latest.lopDays)} day(s) of loss of pay` : ''}. You can open it under <b>Payroll → My payslips</b>. Salary is credited on the last working day of the month.`;
    },
  },
  {
    q: ['notice period', 'resign', 'resignation', 'leaving the company'],
    source: 'Exit & Full-and-Final Settlement Policy',
    answer: () => `Notice periods at SevenMentor:<ul><li>Probation — <b>15 days</b></li><li>Confirmed employees — <b>60 days</b></li><li>Managers and above — <b>90 days</b></li></ul>Buy-out is allowed with department head approval at basic salary for the shortfall. Full and final settlement is released within 45 days of the last working day.`,
  },
  {
    q: ['biometric', 'punch', 'attendance not marked', 'missed punch', 'missing punch', 'regularise'],
    source: 'Attendance SOP · live device status',
    answer: ({ devices }) => {
      const down = (devices ?? []).filter((d) => d.status !== 'ONLINE');
      const note = down.length
        ? `<br><br>Right now <b>${down.map((d) => `${esc(d.code)} at ${esc(d.branch?.shortName ?? '—')}`).join(', ')}</b> ${down.length > 1 ? 'are' : 'is'} not reporting normally, which is a common cause this week.`
        : '';
      return `If a punch is missing, raise a <b>regularisation request</b> from Attendance — pick the affected date and enter the times you actually worked. Your manager approves it and the day is recovered rather than counted as absence.${note}`;
    },
  },
  {
    q: ['holiday', 'holidays', 'next holiday', 'holiday list', 'day off'],
    source: 'Holiday Calendar',
    answer: ({ holidays }) => {
      if (!holidays?.length) return 'No holidays are listed ahead of today in the calendar.';
      const rows = holidays.slice(0, 4)
        .map((h) => `<li><b>${esc(fmtDate(h.date))}</b> (${esc(dayOfWeek(h.date))}) — ${esc(h.name)}${h.isOptional ? ' <span class="muted">(optional)</span>' : ''}</li>`)
        .join('');
      return `The next holidays are:<ul>${rows}</ul>Optional holidays let you choose which ones you take — confirm with HR before planning around them.`;
    },
  },
  {
    q: ['incentive', 'commission', 'target', 'slab'],
    source: 'Incentive & Commission Structure',
    answer: () => `Counselling incentive slabs start at <b>14 conversions</b> for Slab A, 22 for Slab B and 30 for Slab C. Recruitment CRM placement incentives are ₹1,800 per closed placement plus a client-retention bonus. Incentives are paid with the following month's salary, not the same month.`,
  },
  {
    q: ['probation', 'confirmation', 'confirmed'],
    source: 'Employee Handbook',
    answer: () => `Probation runs for <b>90 days</b>. Confirmation needs a manager review plus goal completion above 70%. Once confirmed, Earned Leave accrual begins and the notice period moves from 15 to 60 days.`,
  },
  {
    q: ['pf', 'provident fund', 'uan', 'epf'],
    source: 'Payroll module · statutory records',
    answer: ({ payslips }) => {
      const pf = payslips?.[0]?.deductions?.find((d) => /provident/i.test(d.label ?? d.name ?? ''));
      const amount = pf ? ` — currently <b>${esc(inr(pf.amount ?? pf.value))}</b> a month` : '';
      return `Provident Fund is deducted at <b>12% of basic</b>${amount}, with an equal employer contribution. Your UAN is on your profile under statutory details. Passbook and transfer claims go through the EPFO portal; HR can help with a Form 13 transfer from a previous employer.`;
    },
  },
  {
    q: ['posh', 'harassment', 'complaint', 'internal committee'],
    source: 'POSH Policy & IC Constitution',
    answer: () => `The Internal Committee handles POSH complaints. You can write to <b>ic@sevenmentor.com</b> or speak to any IC member directly — complaints are confidential and the IC responds within 7 days. If you would prefer, I can open a confidential HR ticket for you without any detail written here.`,
  },
  {
    q: ['theme', 'dark mode', 'colour', 'color', 'appearance', 'font', 'how it looks'],
    source: 'Appearance settings · personal preference',
    answer: () => `You can change how PeopleOS looks for yourself — nobody else is affected.<ul><li>Click the <b>palette icon</b> in the top bar, or open <b>My Space</b></li><li>Pick an accent colour, including the SevenMentor orange from the logo</li><li>Switch between <b>light, dark or match-your-device</b></li><li>Choose <b>comfortable or compact</b> spacing</li></ul>Your choice is saved to your account and follows you to any device. Chart colours stay fixed for everyone so shared reports look alike.`,
  },
  {
    q: ['reimbursement', 'travel', 'claim', 'expense'],
    source: 'Travel & Reimbursement Policy',
    answer: () => `Travel claims go under <b>Self-Service → Reimbursements</b> with bills attached, within <b>30 days</b> of the expense. Local travel for client visits is ₹9/km by two-wheeler and ₹16/km by car. Outstation travel needs prior approval from the department head. Approved claims are paid with the next payroll.`,
  },
];

/**
 * Escapes a value before it goes into an answer.
 *
 * Answers are rendered with dangerouslySetInnerHTML, which is what lets them use
 * lists and bold text. The wording is ours, but the values interpolated into it
 * are not: a leave type name, a holiday name and a device code are all typed by
 * somebody through the UI. Without this, an HR user naming a leave type
 * `<img src=x onerror=...>` would run script in every employee's browser the
 * next time they asked the assistant about their balance.
 */
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Builds a tolerant matcher for a keyword phrase.
 *
 * An exact-adjacency match is too brittle for the way people actually type:
 * "how many leaves do I have left" does not contain the literal "how many leave",
 * because of the plural and the words in between. Each word is therefore allowed
 * an inflected ending, and up to three filler words may sit between them. The
 * phrase still has to appear in order, so precision holds.
 */
function phraseRegex(phrase) {
  const words = phrase.trim().split(/\s+/).map(escapeRe);
  const body = words.map((w) => `${w}\\w*`).join('(?:\\s+\\w+){0,3}\\s+');
  return new RegExp(`(^|[^a-z])${body}`, 'i');
}

// Built once — recompiling these on every keystroke would be wasteful.
const COMPILED = KB.map((entry) => ({ entry, patterns: entry.q.map(phraseRegex) }));

export function findAnswer(question, context) {
  // Punctuation becomes whitespace so "work-from-home" and "work from home" are
  // the same question, which is not something a person should have to think about.
  const q = String(question ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  let best = null;
  let bestScore = 0;

  for (const { entry, patterns } of COMPILED) {
    let score = 0;
    patterns.forEach((re, i) => { if (re.test(q)) score += entry.q[i].length; });
    if (score > bestScore) { bestScore = score; best = entry; }
  }

  // Any curated keyword matching is enough to answer. Length only ranks the
  // competing entries — thresholding on it would drop genuinely short terms
  // like "pf", which is exactly what people type.
  if (!best) {
    return {
      html: `I could not find that in the policy library, so I would rather not guess.<br><br>I can answer on leave balances and policy, attendance and biometric problems, payroll and payslips, PF and UAN, insurance, incentives, notice period, POSH, reimbursements and holidays.<br><br>Would you like me to open an HR help desk ticket with your question as written?`,
      offerTicket: true,
    };
  }
  return { html: best.answer(context), source: best.source };
}
